import { createSlice } from '@reduxjs/toolkit'
import {
  fetchUser,
  createUser,
  updateUser,
  uploadProfilePhoto,
  deleteUser,
  deactivateAccount,
  blockAccount,
  unblockAccount,
  fetchPreferences,
  updatePreferences,
  confirmPreferences,
  resetPreferences,
  fetchVerificationStatus,
  sendVerifyEmail,
  confirmVerifyEmail,
  sendVerifyPhone,
  confirmVerifyPhone,
  resetEmailVerification,
  resetPhoneVerification,
  fetchCreatedAdmins,
  fetchAdminProfile,
  updateLanguagePreference
} from '../actions/userActions.js'

const initialState = {
  user: null,
  loading: false,
  error: null,
  uploadLoading: false,
  uploadError: null,
  preferences: null,
  preferencesLoading: false,
  preferencesError: null,
  // Verification states
  verificationStatus: null,
  verificationLoading: false,
  verificationError: null,
  emailVerification: {
    loading: false,
    error: null,
    otpSent: false,
    verified: false
  },
  phoneVerification: {
    loading: false,
    error: null,
    otpSent: false,
    verified: false
  },
  // Created admins state
  createdAdmins: [],
  createdAdminsLoading: false,
  createdAdminsError: null,
  // Admin profile state
  adminProfile: null,
  adminProfileLoading: false,
  adminProfileError: null,
  // Language preference state (for loading/error only)
  languagePreferenceLoading: false,
  languagePreferenceError: null
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUserState: state => {
      state.user = null
      state.loading = false
      state.error = null
      state.uploadLoading = false
      state.uploadError = null
      state.preferences = null
      state.preferencesLoading = false
      state.preferencesError = null
      state.verificationStatus = null
      state.verificationLoading = false
      state.verificationError = null
      state.emailVerification = {
        loading: false,
        error: null,
        otpSent: false,
        verified: false
      }
      state.phoneVerification = {
        loading: false,
        error: null,
        otpSent: false,
        verified: false
      }
      state.createdAdmins = []
      state.createdAdminsLoading = false
      state.createdAdminsError = null
      state.adminProfile = null
      state.adminProfileLoading = false
      state.adminProfileError = null
      state.languagePreferenceLoading = false
      state.languagePreferenceError = null
    },
    clearError: state => {
      state.error = null
      state.uploadError = null
      state.preferencesError = null
      state.verificationError = null
      state.emailVerification.error = null
      state.phoneVerification.error = null
      state.createdAdminsError = null
      state.adminProfileError = null
      state.languagePreferenceError = null
    },
    clearPreferencesError: state => {
      state.preferencesError = null
    },
    clearVerificationError: state => {
      state.verificationError = null
      state.emailVerification.error = null
      state.phoneVerification.error = null
    },
    // Renamed local reducers to avoid name collision with imported thunks
    resetEmailVerificationState: state => {
      state.emailVerification = {
        loading: false,
        error: null,
        otpSent: false,
        verified: false
      }
    },
    resetPhoneVerificationState: state => {
      state.phoneVerification = {
        loading: false,
        error: null,
        otpSent: false,
        verified: false
      }
    },
    updateProfileImage: (state, action) => {
      if (state.user) {
        state.user.profileImage = action.payload.profileImage
        state.user.profileImageUrl = action.payload.profileImageUrl
      }
    },
  },
  extraReducers: builder => {
    builder
      // Fetch user cases
      .addCase(fetchUser.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.user = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.error = action.payload || action.error.message
        state.loading = false
      })

      // Create user cases
      .addCase(createUser.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.user = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(createUser.rejected, (state, action) => {
        // Handle both validation errors (objects) and simple error messages (strings)
        if (typeof action.payload === 'object' && action.payload?.validationErrors) {
          // Keep validation errors as objects for the form to process
          state.error = action.payload
        } else {
          // Ensure simple errors are strings
          state.error = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'An error occurred')
        }
        state.loading = false
      })

      // Update user cases
      .addCase(updateUser.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.user = { ...state.user, ...action.payload }
        state.loading = false
        state.error = null
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload || action.error.message
        state.loading = false
      })

      // Upload profile photo cases
      .addCase(uploadProfilePhoto.pending, state => {
        state.uploadLoading = true
        state.uploadError = null
      })
      .addCase(uploadProfilePhoto.fulfilled, (state, action) => {
        if (state.user) {
          state.user.profileImage = action.payload.profileImage
          state.user.profileImageUrl = action.payload.profileImageUrl || action.payload.imageUrl
        }
        state.uploadLoading = false
        state.uploadError = null
      })
      .addCase(uploadProfilePhoto.rejected, (state, action) => {
        state.uploadError = action.payload || action.error.message
        state.uploadLoading = false
      })

      // Delete user cases
      .addCase(deleteUser.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteUser.fulfilled, state => {
        state.user = null
        state.loading = false
        state.error = null
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload || action.error.message
        state.loading = false
      })

      // Deactivate account cases
      .addCase(deactivateAccount.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deactivateAccount.fulfilled, (state, action) => {
        if (state.user) {
          state.user.status = 'inactive'
        }
        state.loading = false
        state.error = null
      })
      .addCase(deactivateAccount.rejected, (state, action) => {
        state.error = action.payload || action.error.message
        state.loading = false
      })

      // Block account cases
      .addCase(blockAccount.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(blockAccount.fulfilled, (state, action) => {
        if (state.user) {
          state.user.block = true
          state.user.isBlocked = true
        }
        state.loading = false
        state.error = null
      })
      .addCase(blockAccount.rejected, (state, action) => {
        state.error = action.payload || action.error.message
        state.loading = false
      })

      // Unblock account cases
      .addCase(unblockAccount.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(unblockAccount.fulfilled, (state, action) => {
        if (state.user) {
          state.user.block = false
          state.user.isBlocked = false
        }
        state.loading = false
        state.error = null
      })
      .addCase(unblockAccount.rejected, (state, action) => {
        state.error = action.payload || action.error.message
        state.loading = false
      })

      // Fetch preferences cases
      .addCase(fetchPreferences.pending, state => {
        state.preferencesLoading = true
        state.preferencesError = null
      })
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload?.data?.preferences || action.payload
        state.preferencesLoading = false
        state.preferencesError = null
      })
      .addCase(fetchPreferences.rejected, (state, action) => {
        state.preferencesError = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'Failed to fetch preferences')
        state.preferencesLoading = false
      })

      // Update preferences cases
      .addCase(updatePreferences.pending, state => {
        state.preferencesLoading = true
        state.preferencesError = null
      })
      .addCase(updatePreferences.fulfilled, (state, action) => {
        state.preferences = { ...state.preferences, ...action.payload?.data?.preferences || action.payload }
        state.preferencesLoading = false
        state.preferencesError = null
      })
      .addCase(updatePreferences.rejected, (state, action) => {
        state.preferencesError = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'Failed to update preferences')
        state.preferencesLoading = false
      })

      // Confirm preferences cases
      .addCase(confirmPreferences.pending, state => {
        state.preferencesLoading = true
        state.preferencesError = null
      })
      .addCase(confirmPreferences.fulfilled, (state, action) => {
        state.preferences = { ...state.preferences, ...action.payload?.data?.preferences || action.payload }
        state.preferencesLoading = false
        state.preferencesError = null
      })
      .addCase(confirmPreferences.rejected, (state, action) => {
        state.preferencesError = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'Failed to confirm preferences')
        state.preferencesLoading = false
      })

      // Reset preferences cases
      .addCase(resetPreferences.pending, state => {
        state.preferencesLoading = true
        state.preferencesError = null
      })
      .addCase(resetPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload?.data?.preferences || action.payload
        state.preferencesLoading = false
        state.preferencesError = null
      })
      .addCase(resetPreferences.rejected, (state, action) => {
        state.preferencesError = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'Failed to reset preferences')
        state.preferencesLoading = false
      })

      // Fetch verification status cases
      .addCase(fetchVerificationStatus.pending, state => {
        state.verificationLoading = true
        state.verificationError = null
      })
      .addCase(fetchVerificationStatus.fulfilled, (state, action) => {
        state.verificationStatus = action.payload?.data || action.payload
        state.verificationLoading = false
        state.verificationError = null
      })
      .addCase(fetchVerificationStatus.rejected, (state, action) => {
        state.verificationError = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'Failed to fetch verification status')
        state.verificationLoading = false
      })

      // Send email verification cases
      .addCase(sendVerifyEmail.pending, state => {
        state.emailVerification.loading = true
        state.emailVerification.error = null
      })
      .addCase(sendVerifyEmail.fulfilled, (state, action) => {
        state.emailVerification.loading = false
        state.emailVerification.otpSent = true
        state.emailVerification.error = null
      })
      .addCase(sendVerifyEmail.rejected, (state, action) => {
        state.emailVerification.error = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'Failed to send email verification')
        state.emailVerification.loading = false
      })

      // Confirm email verification cases
      .addCase(confirmVerifyEmail.pending, state => {
        state.emailVerification.loading = true
        state.emailVerification.error = null
      })
      .addCase(confirmVerifyEmail.fulfilled, (state, action) => {
        state.emailVerification.loading = false
        state.emailVerification.verified = true
        state.emailVerification.error = null
        // Update user verification status if user data is available
        if (state.user) {
          state.user.emailVerified = true
        }
      })
      .addCase(confirmVerifyEmail.rejected, (state, action) => {
        state.emailVerification.error = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'Failed to verify email')
        state.emailVerification.loading = false
      })

      // Send phone verification cases
      .addCase(sendVerifyPhone.pending, state => {
        state.phoneVerification.loading = true
        state.phoneVerification.error = null
      })
      .addCase(sendVerifyPhone.fulfilled, (state, action) => {
        state.phoneVerification.loading = false
        state.phoneVerification.otpSent = true
        state.phoneVerification.error = null
      })
      .addCase(sendVerifyPhone.rejected, (state, action) => {
        state.phoneVerification.error = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'Failed to send phone verification')
        state.phoneVerification.loading = false
      })

      // Confirm phone verification cases
      .addCase(confirmVerifyPhone.pending, state => {
        state.phoneVerification.loading = true
        state.phoneVerification.error = null
      })
      .addCase(confirmVerifyPhone.fulfilled, (state, action) => {
        state.phoneVerification.loading = false
        state.phoneVerification.verified = true
        state.phoneVerification.error = null
        // Update user verification status if user data is available
        if (state.user) {
          state.user.phoneVerified = true
        }
      })
      .addCase(confirmVerifyPhone.rejected, (state, action) => {
        state.phoneVerification.error = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'Failed to verify phone')
        state.phoneVerification.loading = false
      })

      // Reset email verification cases
      .addCase(resetEmailVerification.fulfilled, (state) => {
        state.emailVerification = {
          loading: false,
          error: null,
          otpSent: false,
          verified: false
        }
      })

      // Reset phone verification cases
      .addCase(resetPhoneVerification.fulfilled, (state) => {
        state.phoneVerification = {
          loading: false,
          error: null,
          otpSent: false,
          verified: false
        }
      })

      // Fetch created admins cases
      .addCase(fetchCreatedAdmins.pending, state => {
        state.createdAdminsLoading = true
        state.createdAdminsError = null
      })
      .addCase(fetchCreatedAdmins.fulfilled, (state, action) => {
        state.createdAdmins = action.payload?.data?.admins || action.payload?.admins || action.payload || []
        state.createdAdminsLoading = false
        state.createdAdminsError = null
      })
      .addCase(fetchCreatedAdmins.rejected, (state, action) => {
        state.createdAdminsError = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'Failed to fetch created admins')
        state.createdAdminsLoading = false
      })

      // Fetch admin profile cases
      .addCase(fetchAdminProfile.pending, state => {
        state.adminProfileLoading = true
        state.adminProfileError = null
      })
      .addCase(fetchAdminProfile.fulfilled, (state, action) => {
        state.adminProfile = action.payload?.admin || action.payload
        state.adminProfileLoading = false
        state.adminProfileError = null
      })
      .addCase(fetchAdminProfile.rejected, (state, action) => {
        state.adminProfileError = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'Failed to fetch admin profile')
        state.adminProfileLoading = false
      })

      // Update language preference cases
      .addCase(updateLanguagePreference.pending, state => {
        state.languagePreferenceLoading = true
        state.languagePreferenceError = null
      })
      .addCase(updateLanguagePreference.fulfilled, (state, action) => {
        // Language preference will be updated in adminProfile when fetchAdminProfile is called
        state.languagePreferenceLoading = false
        state.languagePreferenceError = null
      })
      .addCase(updateLanguagePreference.rejected, (state, action) => {
        state.languagePreferenceError = typeof action.payload === 'string' ? action.payload : (action.error?.message || 'Failed to update language preference')
        state.languagePreferenceLoading = false
      })
  },
})

export const { resetUserState, clearError, clearPreferencesError, clearVerificationError, resetEmailVerificationState, resetPhoneVerificationState, updateProfileImage } = userSlice.actions
export default userSlice.reducer
